/**
 * ScholarshipsShowcase — public landing-page section that surfaces the live
 * scholarship catalog with hero stats and a 3-up featured grid. Pulls from
 * `/api/scholarships-stats` and `/api/scholarships/featured` (both public).
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Award, Banknote, Clock, CheckCircle, Sparkles, ArrowRight, MapPin, GraduationCap, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { scholarshipAPI } from '@/lib/api';

const fmtMoney = (gbp) => {
  if (!gbp) return '—';
  if (gbp >= 1_000_000) return `£${(gbp / 1_000_000).toFixed(1)}M`;
  if (gbp >= 1_000) return `£${(gbp / 1_000).toFixed(0)}K`;
  return `£${gbp}`;
};

const STATUS_LABEL = { open: 'Open', closing_soon: 'Closing soon', rolling: 'Rolling', closed: 'Closed' };

const ScholarshipsShowcase = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [featured, setFeatured] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [s, f] = await Promise.all([
          scholarshipAPI.getStats(),
          scholarshipAPI.getFeatured(),
        ]);
        setStats(s.data);
        setFeatured((f.data || []).slice(0, 3));
      } catch {
        // Silent fail — section just won't show if backend is unreachable
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (!loading && (!stats || stats.total_scholarships === 0)) return null;

  return (
    <section
      className="py-16 sm:py-20 bg-gradient-to-b from-amber-50/40 via-white to-white dark:from-slate-900 dark:via-slate-900 dark:to-slate-900"
      data-testid="landing-scholarships-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider mb-4">
            <Sparkles className="w-3.5 h-3.5" />
            Scholarships
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white leading-tight">
            Don't pay full tuition.<br />
            <span className="bg-gradient-to-r from-amber-500 via-rose-500 to-rose-600 bg-clip-text text-transparent">
              Get scholarships worth {fmtMoney(stats?.total_funding_gbp) || 'millions'}.
            </span>
          </h2>
          <p className="mt-4 text-base sm:text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
            {stats?.total_scholarships ?? '18'} verified, government-backed and university scholarships —
            including Chevening, Fulbright, Knight-Hennessy and more. Free to browse, free to apply.
          </p>
        </div>

        {/* Stats strip */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-10" data-testid="landing-scholarships-stats">
          <StatPill icon={Award} label="Scholarships" value={stats?.total_scholarships ?? '—'} color="from-violet-500 to-purple-500" />
          <StatPill icon={Banknote} label="Total funding" value={fmtMoney(stats?.total_funding_gbp)} color="from-emerald-500 to-teal-500" />
          <StatPill icon={CheckCircle} label="Open now" value={stats?.open_now ?? '—'} color="from-sky-500 to-blue-500" />
          <StatPill icon={Clock} label="Closing soon" value={stats?.closing_soon ?? '—'} color="from-rose-500 to-amber-500" />
        </div>

        {/* Featured grid */}
        {featured.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-10" data-testid="landing-scholarships-featured">
            {featured.map((s) => (
              <FeaturedCard key={s.id} scholarship={s} onClick={() => navigate('/scholarships')} />
            ))}
          </div>
        )}

        {/* CTA */}
        <div className="rounded-2xl bg-gradient-to-br from-teal-600 via-emerald-600 to-cyan-600 p-8 sm:p-10 text-white relative overflow-hidden">
          <div className="absolute -right-12 -top-12 w-48 h-48 bg-white/10 rounded-full blur-2xl pointer-events-none" />
          <div className="absolute -left-12 -bottom-12 w-44 h-44 bg-amber-300/20 rounded-full blur-2xl pointer-events-none" />
          <div className="relative grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-6 items-center">
            <div>
              <h3 className="text-2xl sm:text-3xl font-bold mb-2 leading-tight">Find your scholarship in 60 seconds.</h3>
              <p className="text-white/90 text-sm sm:text-base max-w-xl">
                Filter by destination, degree level, funding type or your profile.
                Apply through H E Direct and a counsellor will guide you free of charge.
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Button
                onClick={() => navigate('/scholarships')}
                className="bg-white text-teal-700 hover:bg-amber-50 font-semibold h-12 px-6"
                data-testid="landing-scholarships-browse-btn"
              >
                Browse All Scholarships <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button
                onClick={() => navigate('/visa-probability')}
                variant="outline"
                className="bg-transparent border-white/40 text-white hover:bg-white/15 hover:text-white font-semibold h-12 px-6"
                data-testid="landing-scholarships-visa-btn"
              >
                Check Visa Odds First
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

function StatPill({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-2xl p-4 flex items-center gap-3 hover:shadow-md transition-shadow">
      <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${color} flex items-center justify-center shrink-0`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      <div className="min-w-0">
        <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white leading-tight">{value}</p>
        <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
      </div>
    </div>
  );
}

function FeaturedCard({ scholarship, onClick }) {
  const status = scholarship.status || 'rolling';
  return (
    <button
      onClick={onClick}
      className="text-left bg-white dark:bg-slate-800 border-2 border-gray-200 dark:border-slate-700 rounded-2xl p-5 hover:border-amber-400 hover:shadow-lg hover:-translate-y-0.5 transition-all group h-full flex flex-col"
      data-testid={`landing-scholarship-card-${scholarship.id}`}
    >
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <Badge className="bg-amber-100 text-amber-800 border-amber-200">
          <Star className="w-3 h-3 mr-1 fill-amber-500" /> Featured
        </Badge>
        <Badge className={
          status === 'open' ? 'bg-emerald-100 text-emerald-700 border-emerald-200' :
          status === 'closing_soon' ? 'bg-rose-100 text-rose-700 border-rose-200' :
          'bg-sky-100 text-sky-700 border-sky-200'
        }>
          {STATUS_LABEL[status]}
        </Badge>
        {scholarship.funding_type === 'Full' && (
          <Badge className="bg-emerald-500 text-white border-0">Fully funded</Badge>
        )}
      </div>
      <h4 className="text-base font-bold text-gray-900 dark:text-white line-clamp-2 group-hover:text-teal-600 transition-colors">
        {scholarship.name}
      </h4>
      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{scholarship.provider}</p>

      <div className="mt-3 space-y-1.5 text-xs text-gray-600 dark:text-gray-300 flex-1">
        <div className="flex items-center gap-1.5">
          <MapPin className="w-3.5 h-3.5 text-blue-500 shrink-0" />
          <span className="truncate">{scholarship.countries?.join(', ')}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <GraduationCap className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
          <span className="truncate">{scholarship.degree_levels?.join(', ')}</span>
        </div>
        {scholarship.est_funding_gbp ? (
          <div className="flex items-center gap-1.5">
            <Banknote className="w-3.5 h-3.5 text-amber-500 shrink-0" />
            <span className="font-medium text-gray-800 dark:text-gray-100">Worth up to {fmtMoney(scholarship.est_funding_gbp)}</span>
          </div>
        ) : null}
      </div>

      <div className="mt-4 inline-flex items-center text-xs font-semibold text-teal-600 group-hover:text-teal-700">
        View details <ArrowRight className="w-3.5 h-3.5 ml-1 group-hover:translate-x-0.5 transition-transform" />
      </div>
    </button>
  );
}

export default ScholarshipsShowcase;
